import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../../../Common/Dialog/Dialog';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogButton from '../../../Common/Buttons/DialogButton';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogTitle from '../../../Common/Dialog/DialogTitle';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import { EditProductListing, ResetProductListings, SelectProductListing } from '../../../UserData/_actions/Store/StoreProductListingActions';
import { FetchOneItem } from '../../../UserData/_actions/UserDataActions';
import { FetchUserStorePresets } from '../../../UserData/_actions/Store/UserStorePresetActions';
import EditProduct from './EditProduct';
import Publishing from './Publishing';
import SelectStore from './SelectStore';
import buildDefaultProductListing from './Utils/buildDefaultProductListing';
import type ListingStore from '../../../UserData/_stores/Store/ListingStore';
import usePublishListings from '../../../Common/_hooks/Store/usePublishListings';

interface Props {
	opened: boolean
	productDesignId: number
	onClose: () => void
}

export default function PublishProductDesignDialog(props: Props) {
	const [t] = useTranslation('account-v2');
	const dispatch = useAppDispatch();
	const publishListings = usePublishListings();

	const listings = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings'));
	const productDesign = useAppSelector(state => state.get('userData').get('productDesigns').get('data').get(String(props.productDesignId)));
	const product = useAppSelector(state => productDesign ? state.get('appData').get('products').get(String(productDesign.get('id_product'))) : undefined);
	const storePreset = useAppSelector(state => productDesign ? state.get('userData').get('storePresets').get(productDesign.get('id_product')) : undefined);

	const [currentStep, setCurrentStep] = useState(0);
	const [selectedStore, setSelectedStore] = useState('');
	const [closeDialogOpened, setCloseDialogOpened] = useState(false);
	const [productDesignLoaded, setProductDesignLoaded] = useState(false);
	const [storePresetsLoaded, setStorePresetsLoaded] = useState(false);
	const [listingInitialized, setListingInitialized] = useState(false);
	const [publishBatchId, setPublishBatchId] = useState('');

	const handlePublishListing = useCallback((formListing: ListingStore, listing: ListingStore) => {
		const updatedListing = listing.withMutations(tmpListing => {
			tmpListing.set('title', formListing.get('title'));
			tmpListing.set('description', formListing.get('description'));
			tmpListing.set('price', formListing.get('price'));
			tmpListing.set('update_preset', formListing.get('update_preset'));
			tmpListing.set('is_modified', true);
			tmpListing.set('disabled_preview_ids', formListing.get('disabled_preview_ids'));
			tmpListing.set('disabled_option_ids', formListing.get('disabled_option_ids'));
			tmpListing.set('variant_prices', formListing.get('variant_prices'));
			tmpListing.set('squarespace_store_page_id', formListing.get('squarespace_store_page_id'));
		});

		dispatch(EditProductListing(
			updatedListing.get('id_product_design'),
			updatedListing.get('id_product'),
			updatedListing.get('title'),
			updatedListing.get('description'),
			updatedListing.get('price'),
			updatedListing.get('update_preset'),
			updatedListing.get('is_modified'),
			updatedListing.get('disabled_preview_ids').toArray(),
			updatedListing.get('disabled_option_ids').toArray(),
			updatedListing.get('variant_prices'),
			updatedListing.get('squarespace_store_page_id'),
		));

		publishListings(selectedStore, listings.set(updatedListing.get('id'), updatedListing), {
			onSuccess: (response) => {
				setPublishBatchId(response.batch_id);
				setCurrentStep(2);
			},
		});
	}, [dispatch, listings, publishListings, selectedStore]);

	const resetDialogState = useCallback(() => {
		setCurrentStep(0);
		setSelectedStore('');
		setProductDesignLoaded(false);
		setStorePresetsLoaded(false);
		setListingInitialized(false);
		setPublishBatchId('');
		dispatch(ResetProductListings());
	}, [dispatch]);

	const handleCloseModal = useCallback(() => {
		setCloseDialogOpened(false);
		props.onClose();
	}, [props.onClose]);

	const onCloseDialogOpen = useCallback(() => setCloseDialogOpened(true), []);
	const onCloseDialogClose = useCallback(() => setCloseDialogOpened(false), []);

	const onDialogClose = useCallback(() => {
		if (currentStep > 0 && currentStep < 2) {
			onCloseDialogOpen();
			return;
		}

		handleCloseModal();
	}, [currentStep, handleCloseModal, onCloseDialogOpen]);

	const onSelectStore = useCallback((store: string) => {
		setSelectedStore(store);
		setCurrentStep(1);
	}, []);

	useEffect(() => {
		if (!props.opened) {
			return;
		}

		resetDialogState();

		dispatch(FetchOneItem(props.productDesignId, 'productDesigns').set({
			onSuccess: () => setProductDesignLoaded(true),
			onFailure: () => setProductDesignLoaded(true),
		}));
		dispatch(FetchUserStorePresets().set({
			onSuccess: () => setStorePresetsLoaded(true),
			onFailure: () => setStorePresetsLoaded(true),
		}));
	}, [dispatch, props.opened, props.productDesignId, resetDialogState]);

	useEffect(() => {
		if (!props.opened
			|| listingInitialized
			|| !productDesignLoaded
			|| !storePresetsLoaded
			|| !productDesign
			|| !product
		) {
			return;
		}

		const listing = buildDefaultProductListing(productDesign, product, storePreset);
		dispatch(EditProductListing(
			listing.id_product_design,
			listing.id_product,
			listing.title,
			listing.description,
			listing.price,
			listing.update_preset,
			listing.is_modified,
			listing.disabled_preview_ids,
			listing.disabled_option_ids,
			listing.variant_prices,
		));
		dispatch(SelectProductListing(listing.id));
		setListingInitialized(true);
	}, [dispatch, listingInitialized, product, productDesign, productDesignLoaded, props.opened, storePreset, storePresetsLoaded]);

	const body = useMemo(() => {
		switch (currentStep) {
			case 0:
				return <SelectStore
					ready={listingInitialized}
					onSelectStore={onSelectStore}
					onDialogClose={onDialogClose}
				/>;
			case 1:
				return <EditProduct
					store={selectedStore}
					index={currentStep}
					handleStep={setCurrentStep}
					isSingle
					backButtonLabel={t('Back')}
					cancelBehavior="close"
					submitLabel={t('Publish product')}
					submitColor="accent"
					showStorePageField={selectedStore === 'squarespace'}
					onSubmitListing={handlePublishListing}
					onDialogClose={onDialogClose}
					onDialogCloseWithConfirm={onDialogClose}
				/>;
			case 2:
				return <Publishing
					store={selectedStore}
					batchId={publishBatchId}
					onDialogClose={onDialogClose}
				/>;
			default:
				return null;
		}
	}, [currentStep, handlePublishListing, onDialogClose, onSelectStore, selectedStore, listingInitialized, publishBatchId, t]);

	const isSmallDialog = currentStep === 0 || currentStep === 2;

	return <>
		<Dialog
			open={props.opened}
			maxWidth={isSmallDialog ? 'sm' : 'lg'}
			fullWidth
			onClose={onDialogClose}
			TransitionProps={{
				onExited: resetDialogState,
			}}
			showCloseButton
			hasTabs={currentStep === 1}
		>
			{body}
		</Dialog>

		<Dialog
			open={closeDialogOpened}
			maxWidth="xs"
			fullWidth
			onClose={onCloseDialogClose}
			showCloseButton
		>
			<DialogTitle>{t('Are you sure?')}</DialogTitle>
			<DialogContent>
				<p>{t('You will lose your changes if you close this dialog.')}</p>
			</DialogContent>
			<DialogActions>
				<DialogButton
					color="secondary"
					onClick={onCloseDialogClose}
				>{t('Cancel')}</DialogButton>
				<DialogButton
					color="primary"
					onClick={handleCloseModal}
					autoFocus
				>{t('Close')}</DialogButton>
			</DialogActions>
		</Dialog>
	</>;
}
